import { useEffect, useRef, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import {
  Box,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
  Checkbox,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Spacer,
  Divider,
  Button,
  Heading,
  useColorModeValue,
} from '@chakra-ui/react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useToastNotif from 'hooks/useToastNotif';
import { TOAST_SUCCESS } from 'utils/constants/toast';

import { ReducerStatus } from 'utils/constants';
import { arrayOfString } from 'utils/jsonParser';

import SubQuestForm from 'components/subQuest/SubQuestForm';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import ResultModal from 'components/common/modals/ResultModal';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';
import StayOnPageModal from 'components/common/modals/StayOnPageModal';
import QuestionAdminList from 'components/question/admin/QuestionAdminList';
import Breadcrumbs from 'components/common/Breadcrumbs';

import { subquestActions } from 'store/modules/subquest/subquest-slice';
import {
  createQuizSubQuest,
  createQuizSubQuestQuestions,
} from 'store/modules/subquest/subquest-action';
import { fetchQuestByIdAction } from 'store/modules/quest/quest-actions';
import {
  convertTimeBeforeNextAttemptToSeconds,
  quizSettingsValidation,
} from 'utils/quizHelper';

import { CREATE_MODE } from 'utils/constants/operationMode';
import importQuestionsFromCsv from 'utils/questions/importQuestionsCsv';
import { QuizConfirmationDescription } from 'components/subQuest/quizzes/QuizConfirmationDescription';

function CreateQuizPage() {
  document.title = `Skill Quest | Create Quiz`;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formikForm = useRef();

  const { generateToastNotif } = useToastNotif();

  const footerBg = useColorModeValue('white', 'gray.900');

  const topRef = useRef(null);

  const [triggerSubQuestForm, setTriggerSubQuestForm] = useState(0);
  const [isSubQuestFormValid, setIsSubQuestFormValid] = useState(false);
  const [isFormSubmit, setIsFormSubmit] = useState(false);
  const [isSaveSettings, setIsSaveSettings] = useState(false);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [showReturnConfirmation, setShowReturnConfirmation] = useState(false);
  const [showImportConfirmation, setShowImportConfirmation] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importStatus, setImportStatus] = useState(ReducerStatus.IDLE);

  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [saveConfirmationStatus, setSaveConfirmationStatus] = useState(
    ReducerStatus.IDLE
  );
  const [clearConfirmationStatus, setClearConfirmationStatus] = useState(
    ReducerStatus.IDLE
  );
  const [createdSubQuestId, setCreatedSubQuestId] = useState(null);
  const [subQuestFormInputs, setSubQuestFormInputs] = useState({
    name: '',
    objective: '',
    code: '',
    isPublished: '',
    tags: [],
    groups: [],
  });

  const [formInputs, setFormInputs] = useState({
    scoreType: 'Actual Score',
    hasTimeLimit: true,
    timeLimit: 5,
    passingScore: 1,
    isShuffled: true,
    showAnswers: true,
    showResult: true,
    unlimitedAttempts: true,
    numberOfAttempts: 3,
    hasIntervalBeforeNextAttempt: false,
    intervalBeforeNextAttempt: 60,
    fetchFromQuestionBank: false,
    totalMark: 0,
  });

  const [timeBeforeNextAttempt, setTimeBeforeNextAttempt] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
  });
  const [questions, setQuestions] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [forceCollapseAll, setForceCollapseAll] = useState(false);
  const [forceExpandAll, setForceExpandAll] = useState(false);
  const [allQuestionsCollapsed, setAllQuestionsCollapsed] = useState(false);

  const courseId = searchParams.get('courseId');
  const questId = searchParams.get('questId');

  // Error states for ConfirmationActionModal for local storage settings
  const [saveSettingsError, setSaveSettingsError] = useState(null);
  const [clearSettingsError, setClearSettingsError] = useState(null);

  const [formResetTrigger, setFormResetTrigger] = useState(0);

  const createQuiz = useSelector((state) => state.subquest.createQuiz);
  const quest = useSelector((state) => state.subquest.quest);
  const createQuizQuestions = useSelector(
    (state) => state.subquest.createQuizQuestions
  );

  useEffect(() => {
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const handleAddQuestion = () => {
    const newQuestion = {
      id: null,
      tempId: uuidv4(),
      questionHtml: '',
      questionTypeId: 1,
      multipleChoiceChoices: [
        { option: 'A', value: 'A' },
        { option: 'B', value: 'B' },
      ],
      multipleChoiceAnswers: [],
      trueOrFalseAnswer: true,
      explanation: '',
      mark: 1,
      allowPartialPoints: false,
      errors: ['Question cannot be blank. '],
      autoCheckInstruction: '',
      isAllowedAutoCheck: true,
    };
    const newQuestions = [...questions, newQuestion];
    setQuestions(newQuestions);
  };

  const handleTabsChange = (index) => {
    if (index < 0 || index > 2) return;
    setTabIndex(index);
  };

  const handleUpdateQuestion = (index, updatedQuestion) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = updatedQuestion;
    setQuestions(updatedQuestions);
  };

  const handleQuestionTypeChange = (index, question, newQuestionTypeId) => {
    const updatedQuestion = {
      ...question,
      questionTypeId: parseInt(newQuestionTypeId, 10),
    };
    handleUpdateQuestion(index, updatedQuestion);
  };

  const handleRemoveQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleQuestionsReorder = (reorderedQuestions) => {
    setQuestions(reorderedQuestions);
  };

  const handleCollapseAll = () => {
    setForceCollapseAll(true);

    // Reset the trigger after a brief moment to allow for future collapses
    setTimeout(() => {
      setForceCollapseAll(false);
    }, 100);
  };

  const handleExpandAll = () => {
    setForceExpandAll(true);

    // Reset the trigger after a brief moment to allow for future expands
    setTimeout(() => {
      setForceExpandAll(false);
    }, 100);
  };

  const handleToggleCollapseExpand = () => {
    if (allQuestionsCollapsed) {
      handleExpandAll();
      setAllQuestionsCollapsed(false);
    } else {
      handleCollapseAll();
      setAllQuestionsCollapsed(true);
    }
  };

  useEffect(() => {
    if (quest?.ownerGroups) {
      setSubQuestFormInputs((prev) => ({
        ...prev,
        groups: arrayOfString(quest?.ownerGroups),
      }));
    }
  }, [quest?.ownerGroups, formResetTrigger]);

  // Fetch quest data from URL parameter if not present in Redux
  useEffect(() => {
    if (questId && !quest) {
      dispatch(fetchQuestByIdAction(questId))
        .unwrap()
        .then((questData) => {
          dispatch(subquestActions.setQuest(questData));
        });
    }
  }, [searchParams, questId, quest, dispatch]);

  const handleSubmitForm = async (values) => {
    if (isSubQuestFormValid === false) return;

    // Store the form values for confirmation
    setPendingFormValues(values);
    setShowSubmitConfirmation(true);
  };

  const handleConfirmSubmission = async () => {
    if (!pendingFormValues) return;

    setFormInputs({ ...pendingFormValues });
    let interval = pendingFormValues.intervalBeforeNextAttempt;
    let time = pendingFormValues.timeLimit;

    if (pendingFormValues.hasIntervalBeforeNextAttempt) {
      interval = convertTimeBeforeNextAttemptToSeconds(timeBeforeNextAttempt);
    }
    if (pendingFormValues.hasTimeLimit === false) {
      time = 1;
    }

    const mergedObject = {
      ...formInputs,
      ...subQuestFormInputs,
      timeLimit: time,
      intervalBeforeNextAttempt: interval,
      ownerGroups: subQuestFormInputs.groups,
      isPublished: subQuestFormInputs.isPublished === 'true',
      totalMark: questions.reduce((sum, question) => sum + question.mark, 0),
    };

    const newQuizSubQuest = await dispatch(
      createQuizSubQuest({ questId: quest.id, quizSubQuest: mergedObject })
    );

    const newQuizSubQuestId = newQuizSubQuest.payload?.subQuest?.subQuest?.id;

    if (newQuizSubQuestId) {
      setCreatedSubQuestId(newQuizSubQuestId);
    }

    if (questions.length === 0) return;

    const totalErrorCount = questions.reduce((acc, entry) => {
      return acc + entry.errors.length;
    }, 0);

    if (totalErrorCount !== 0) return;

    const createdSubQuestQuizId =
      newQuizSubQuest.payload?.subQuest?.subQuest?.subQuestQuiz?.id;

    questions.forEach((question, index) => {
      const updatedQuestion = question;
      updatedQuestion.SubQuestQuizId = createdSubQuestQuizId;
      updatedQuestion.position = index;
      handleUpdateQuestion(index, updatedQuestion);
    });

    dispatch(createQuizSubQuestQuestions({ questions }));

    setShowSubmitConfirmation(false);
    setPendingFormValues(null);
  };

  const handleCancelSubmitConfirmation = () => {
    setShowSubmitConfirmation(false);
    setPendingFormValues(null);
  };

  const handleSubmitConfirmationResult = () => {
    setShowSubmitConfirmation(false);
  };

  const handleTriggerSubmit = () => {
    setTriggerSubQuestForm((trigger) => trigger + 1);
    setIsFormSubmit(true);
  };

  useEffect(() => {
    if (isFormSubmit && isSubQuestFormValid) {
      formikForm?.current?.submitForm();
      setIsFormSubmit(false);
    }
  }, [isFormSubmit, isSubQuestFormValid, setIsFormSubmit]);

  // Handle localStorage save when isSaveSettings is true
  // Only save after subQuestFormInputs has been updated by SubQuestForm
  useEffect(() => {
    if (isSaveSettings) {
      // Check if subQuestFormInputs has actually been updated (not empty initial state)
      const hasValidSubQuestData =
        subQuestFormInputs.name ||
        subQuestFormInputs.objective ||
        subQuestFormInputs.code;

      if (hasValidSubQuestData) {
        const payload = {
          formInputs,
          subQuestFormInputs,
          questions,
          tabIndex,
          timeBeforeNextAttempt,
        };

        try {
          const storageKey = questId
            ? `createQuizDraft_${questId}`
            : 'createQuizDraft';
          localStorage.setItem(storageKey, JSON.stringify(payload));
        } catch (err) {
          setSaveSettingsError(err);
        }

        // Reset the flag only after successful save
        setIsSaveSettings(false);
      }
    }
  }, [
    isSaveSettings,
    formInputs,
    subQuestFormInputs,
    questions,
    tabIndex,
    questId,
    timeBeforeNextAttempt,
  ]);

  const handleShowReturnConfirmation = () => {
    setShowReturnConfirmation(true);
  };

  const handleConfirmReturn = () => {
    setShowReturnConfirmation(false);
    setFormInputs({
      scoreType: 'Actual Score',
      hasTimeLimit: true,
      timeLimit: 5,
      passingScore: 1,
      isShuffled: true,
      showAnswers: true,
      showResult: true,
      unlimitedAttempts: true,
      numberOfAttempts: 3,
      hasIntervalBeforeNextAttempt: false,
      intervalBeforeNextAttempt: 60,
      fetchFromQuestionBank: false,
      totalMark: 0,
    });

    setSubQuestFormInputs({
      name: '',
      objective: '',
      code: '',
      isPublished: '',
      tags: [],
      emails: [],
    });

    setTabIndex(0);
    setTriggerSubQuestForm(-1);
    setIsSubQuestFormValid(false);
    setQuestions([]);

    setFormResetTrigger((prev) => prev + 1);
    navigate(`/admin/courses/${courseId}`);
  };

  const handleCancelReturn = () => {
    setShowReturnConfirmation(false);

    if (createdSubQuestId) {
      navigate(
        `/admin/quizzes/edit/${createdSubQuestId}?courseId=${courseId}&questId=${questId}`
      );
    }
  };

  const handleResult = () => {
    if (createQuiz.status === ReducerStatus.SUCCEEDED) {
      // Clear localStorage draft after successful submission
      try {
        const storageKey = questId
          ? `createQuizDraft_${questId}`
          : 'createQuizDraft';
        localStorage.removeItem(storageKey);
      } catch (err) {
        setClearSettingsError(err);
      }

      dispatch(subquestActions.resetCreateQuiz());
      handleShowReturnConfirmation();
    }
  };

  // Load draft from localStorage on mount
  useEffect(() => {
    try {
      const storageKey = questId
        ? `createQuizDraft_${questId}`
        : 'createQuizDraft';
      const savedDraft = localStorage.getItem(storageKey);
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        setFormInputs(draft.formInputs);
        setSubQuestFormInputs(draft.subQuestFormInputs);
        setQuestions(draft.questions || []);
        setTabIndex(draft.tabIndex || 0);
        if (draft.timeBeforeNextAttempt) {
          setTimeBeforeNextAttempt(draft.timeBeforeNextAttempt);
        }
      }
    } catch (err) {
      // Silently handle localStorage errors
    }
  }, [questId]);

  // Save draft to localStorage
  const handleSaveDraft = () => {
    setShowSaveConfirmation(true);
  };

  const handleConfirmSave = () => {
    setSaveConfirmationStatus(ReducerStatus.LOADING);

    // Set flag to indicate this is a save operation, not a submit
    setIsSaveSettings(true);
    // Trigger SubQuest form submission to capture current data
    setTriggerSubQuestForm((trigger) => trigger + 1);

    // Simulate async operation for consistency
    setTimeout(() => {
      setSaveConfirmationStatus(ReducerStatus.SUCCEEDED);
    }, 500);
  };

  const handleCancelSaveConfirmation = () => {
    setShowSaveConfirmation(false);
  };

  const handleSaveConfirmationResult = () => {
    setShowSaveConfirmation(false);
    setSaveConfirmationStatus(ReducerStatus.IDLE);
    generateToastNotif(
      TOAST_SUCCESS,
      'Draft Saved',
      "Quiz draft saved successfully to your browser's local storage.",
      true
    );
  };

  const handleClearAll = () => {
    setShowClearConfirmation(true);
  };

  const handleConfirmClear = () => {
    setClearConfirmationStatus(ReducerStatus.LOADING);

    setTimeout(() => {
      setFormInputs({
        scoreType: 'Actual Score',
        hasTimeLimit: true,
        timeLimit: 5,
        passingScore: 1,
        isShuffled: true,
        showAnswers: true,
        showResult: true,
        unlimitedAttempts: true,
        numberOfAttempts: 3,
        hasIntervalBeforeNextAttempt: false,
        intervalBeforeNextAttempt: 60,
        fetchFromQuestionBank: false,
        totalMark: 0,
      });
      setSubQuestFormInputs({
        name: '',
        objective: '',
        code: '',
        isPublished: '',
        tags: [],
        groups: [],
      });
      setQuestions([]);
      setTabIndex(0);
      try {
        const storageKey = questId
          ? `createQuizDraft_${questId}`
          : 'createQuizDraft';
        localStorage.removeItem(storageKey);
      } catch (err) {
        // Silently handle localStorage errors
      }
      setClearConfirmationStatus(ReducerStatus.SUCCEEDED);
    }, 500);
  };

  const handleCancelClearConfirmation = () => {
    setShowClearConfirmation(false);
  };

  const handleClearConfirmationResult = () => {
    setShowClearConfirmation(false);
    setClearConfirmationStatus(ReducerStatus.IDLE);
    generateToastNotif(
      TOAST_SUCCESS,
      'Draft Cleared',
      "Quiz draft cleared successfully from your browser's local storage.",
      true
    );
  };

  /**
   * Handles importing quiz questions from CSV file
   * Processes the file and shows confirmation modal with preview
   */
  const handleImportQuestions = async (file) => {
    setImportStatus(ReducerStatus.LOADING);

    try {
      const result = await importQuestionsFromCsv(file);

      setImportData(result);
      setImportErrors(result.errors);
      setImportStatus(ReducerStatus.IDLE);
      setShowImportConfirmation(true);
    } catch (error) {
      setImportErrors([error.message]);
      setImportStatus(ReducerStatus.FAILED);
      setShowImportConfirmation(true);
    }
  };

  /**
   * Confirms the import and adds questions to existing list
   * Updates positions for all questions after import
   */
  const handleConfirmImport = () => {
    if (!importData || !importData.questions) return;

    setImportStatus(ReducerStatus.LOADING);

    const currentQuestionsCount = questions.length;

    // Update positions for imported questions to append after existing ones
    const questionsWithUpdatedPositions = importData.questions.map(
      (question, index) => ({
        ...question,
        position: currentQuestionsCount + index,
      })
    );

    // Add imported questions to existing questions
    const updatedQuestions = [...questions, ...questionsWithUpdatedPositions];
    setQuestions(updatedQuestions);

    setImportStatus(ReducerStatus.SUCCEEDED);

    generateToastNotif(
      TOAST_SUCCESS,
      'Questions Imported',
      `Successfully imported ${importData.questions.length} question(s) to your quiz.`,
      true
    );

    // Close import modal and reset state
    setShowImportConfirmation(false);
    setImportData(null);
    setImportErrors([]);
    setImportStatus(ReducerStatus.IDLE);
  };

  /**
   * Cancels the import process and resets import state
   */
  const handleCancelImport = () => {
    setShowImportConfirmation(false);
    setImportData(null);
    setImportErrors([]);
    setImportStatus(ReducerStatus.IDLE);
  };

  /**
   * Handles the result of import confirmation modal
   */
  const handleImportConfirmationResult = () => {
    setShowImportConfirmation(false);
    setImportData(null);
    setImportErrors([]);
    setImportStatus(ReducerStatus.IDLE);
  };

  return (
    <Box>
      {/* Submit Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showSubmitConfirmation}
        onClose={handleCancelSubmitConfirmation}
        onConfirm={handleConfirmSubmission}
        onSubmitResult={handleSubmitConfirmationResult}
        status={createQuiz.status}
        successMessage="The quiz sub-quest has been created successfully."
        error={createQuiz.error}
      >
        <QuizConfirmationDescription
          type="submit"
          mode="create"
          data={{
            pendingFormValues,
            questions,
            subQuestFormInputs,
          }}
        />
      </ConfirmationActionModal>

      {/* Save Settings Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showSaveConfirmation}
        onClose={handleCancelSaveConfirmation}
        onConfirm={handleConfirmSave}
        onSubmitResult={handleSaveConfirmationResult}
        status={saveConfirmationStatus}
        successMessage="Your quiz progress has been saved successfully."
        error={saveSettingsError}
      >
        <QuizConfirmationDescription
          type="save"
          data={{
            questions,
          }}
        />
      </ConfirmationActionModal>

      {/* Clear Settings Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showClearConfirmation}
        onClose={handleCancelClearConfirmation}
        onConfirm={handleConfirmClear}
        onSubmitResult={handleClearConfirmationResult}
        status={clearConfirmationStatus}
        successMessage="All quiz settings and questions have been cleared successfully."
        error={clearSettingsError}
      >
        <QuizConfirmationDescription
          type="clear"
          mode="create"
          data={{
            questions,
            subQuestFormInputs,
          }}
        />
      </ConfirmationActionModal>

      {/* Return to Admin Course Page Confirmation Modal */}
      <StayOnPageModal
        isOpen={showReturnConfirmation}
        onClose={handleCancelReturn}
        onConfirm={handleConfirmReturn}
        title="Quiz Created Successfully!"
        message="Your quiz has been created successfully. Would you like to stay on page and continue editing the quiz or return to the admin course page?"
        proceedButtonText="Return to Course"
      />

      {/* Import Questions Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showImportConfirmation}
        onClose={handleCancelImport}
        onConfirm={handleConfirmImport}
        onSubmitResult={handleImportConfirmationResult}
        status={importStatus}
        successMessage={`Successfully imported ${importData?.summary?.validQuestions || 0} questions.`}
        error={importStatus === ReducerStatus.FAILED ? importErrors[0] : null}
        confirmDisabled={
          importStatus === ReducerStatus.FAILED ||
          (importData?.summary?.validQuestions || 0) === 0
        }
      >
        <QuizConfirmationDescription
          type="import"
          data={{
            importStatus,
            importData,
            importErrors,
            questions,
          }}
        />
      </ConfirmationActionModal>

      <Flex minH="100vh" ml={{ base: 0, md: 20 }} ref={topRef}>
        <Box flex="1" ml={2} p="5">
          <Flex direction={{ base: 'column', lg: 'row' }}>
            <Box flex="1">
              <Breadcrumbs
                mb={4}
                links={[
                  { name: 'Home', path: '/' },
                  { name: 'Courses', path: '/admin/courses' },
                  {
                    name: quest?.name,
                    path: `/admin/courses/${courseId}`,
                  },
                  {
                    name: 'Create Quiz',
                    path: `/admin/quizzes/create?courseId=${courseId}&questId=${questId}`,
                  },
                ]}
              />
              <Heading as="h1" size="md" mb={4}>
                Create Quiz Sub-Quest
              </Heading>
              <Tabs
                variant="enclosed"
                index={tabIndex}
                onChange={handleTabsChange}
              >
                <TabList>
                  <Tab>General</Tab>
                  <Tab>Settings</Tab>
                  <Tab>Questions</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <SubQuestForm
                      questName={quest?.name}
                      groups={quest?.ownerGroups}
                      subQuestFormInputs={subQuestFormInputs}
                      triggerSubQuestForm={triggerSubQuestForm}
                      setSubQuestFormInputs={setSubQuestFormInputs}
                      setIsSubQuestFormValid={setIsSubQuestFormValid}
                      isSaveSettings={isSaveSettings}
                    />
                  </TabPanel>
                  <TabPanel>
                    <Formik
                      enableReinitialize
                      innerRef={formikForm}
                      initialValues={formInputs}
                      onSubmit={(values) => {
                        handleSubmitForm(values);
                      }}
                      validationSchema={quizSettingsValidation}
                    >
                      {() => (
                        <Box>
                          <Form>
                            <Flex>
                              <Box flex="1" mr={5}>
                                <Flex mt={5}>
                                  <Box>
                                    <Field name="isShuffled">
                                      {({ field, form }) => (
                                        <FormikFormControl
                                          errors={form.errors.isShuffled}
                                          touched={form.touched.isShuffled}
                                          form={form}
                                          label=""
                                          helperText=""
                                        >
                                          <Checkbox
                                            size="lg"
                                            mr={5}
                                            isChecked={field.value}
                                            onChange={(e) =>
                                              setFormInputs({
                                                ...formInputs,
                                                isShuffled: e.target.checked,
                                              })
                                            }
                                          >
                                            Shuffle Questions
                                          </Checkbox>
                                          <Text fontSize="sm" color="gray">
                                            Randomize the order of questions
                                            each time the quiz is taken.
                                          </Text>
                                        </FormikFormControl>
                                      )}
                                    </Field>
                                  </Box>
                                </Flex>
                                <Flex align="center" mt={4}>
                                  <Box w="50%">
                                    <Field name="hasTimeLimit">
                                      {({ field, form }) => (
                                        <FormikFormControl
                                          form={form}
                                          label=""
                                          helperText=""
                                          errors={form.errors.hasTimeLimit}
                                          touched={form.touched.hasTimeLimit}
                                        >
                                          <Checkbox
                                            size="lg"
                                            mr={5}
                                            isChecked={field.value}
                                            onChange={(e) =>
                                              setFormInputs({
                                                ...formInputs,
                                                hasTimeLimit: e.target.checked,
                                              })
                                            }
                                          >
                                            Time Limit
                                          </Checkbox>
                                        </FormikFormControl>
                                      )}
                                    </Field>
                                  </Box>
                                  <Box>
                                    {formInputs.hasTimeLimit === true && (
                                      <Field name="timeLimit">
                                        {({ field, form }) => (
                                          <FormikFormControl
                                            errors={form.errors.timeLimit}
                                            touched={form.touched.timeLimit}
                                            form={form}
                                            label=""
                                            helperText=""
                                          >
                                            <NumberInput
                                              min={0}
                                              max={100}
                                              w="100px"
                                              onChange={(value) =>
                                                setFormInputs({
                                                  ...formInputs,
                                                  timeLimit:
                                                    value === ''
                                                      ? 0
                                                      : parseInt(value, 10),
                                                })
                                              }
                                              value={field.value}
                                            >
                                              <NumberInputField />
                                              <NumberInputStepper>
                                                <NumberIncrementStepper />
                                                <NumberDecrementStepper />
                                              </NumberInputStepper>
                                            </NumberInput>
                                          </FormikFormControl>
                                        )}
                                      </Field>
                                    )}
                                  </Box>
                                  {formInputs.hasTimeLimit === true && (
                                    <Box ml={2}>minutes</Box>
                                  )}
                                </Flex>
                                <Flex mt={-2}>
                                  <Text fontSize="sm" color="gray">
                                    Check this box if you want to impose a time
                                    limit on the quiz
                                  </Text>
                                </Flex>
                                <Flex align="center" mt={5}>
                                  <Box w="50%">
                                    <Field name="unlimitedAttempts">
                                      {({ field, form }) => (
                                        <FormikFormControl
                                          errors={form.errors.unlimitedAttempts}
                                          touched={
                                            form.touched.unlimitedAttempts
                                          }
                                          form={form}
                                          label=""
                                          helperText=""
                                        >
                                          <Checkbox
                                            size="lg"
                                            mr={5}
                                            isChecked={field.value}
                                            onChange={(e) =>
                                              setFormInputs({
                                                ...formInputs,
                                                unlimitedAttempts:
                                                  e.target.checked,
                                              })
                                            }
                                          >
                                            Unlimited Attempts
                                          </Checkbox>
                                        </FormikFormControl>
                                      )}
                                    </Field>
                                  </Box>
                                  <Box>
                                    {formInputs.unlimitedAttempts === false && (
                                      <Field name="numberOfAttempts">
                                        {({ field, form }) => (
                                          <FormikFormControl
                                            errors={
                                              form.errors.numberOfAttempts
                                            }
                                            touched={
                                              form.touched.numberOfAttempts
                                            }
                                            form={form}
                                            label=""
                                            helperText=""
                                          >
                                            <NumberInput
                                              min={0}
                                              w="100px"
                                              onChange={(value) =>
                                                setFormInputs({
                                                  ...formInputs,
                                                  numberOfAttempts:
                                                    value === ''
                                                      ? 0
                                                      : parseInt(value, 10),
                                                })
                                              }
                                              value={field.value}
                                            >
                                              <NumberInputField {...field} />
                                              <NumberInputStepper>
                                                <NumberIncrementStepper />
                                                <NumberDecrementStepper />
                                              </NumberInputStepper>
                                            </NumberInput>
                                          </FormikFormControl>
                                        )}
                                      </Field>
                                    )}
                                  </Box>
                                  {formInputs.unlimitedAttempts === false && (
                                    <Box ml={2}>attempts</Box>
                                  )}
                                </Flex>
                                <Flex>
                                  <Text fontSize="sm" color="gray">
                                    Allow participants to retake the quiz an
                                    unlimited number of times.
                                  </Text>
                                </Flex>
                                <Flex mt={5}>
                                  <Box>
                                    <Field name="hasIntervalBeforeNextAttempt">
                                      {({ field, form }) => (
                                        <FormikFormControl
                                          errors={
                                            form.errors
                                              .hasIntervalBeforeNextAttempt
                                          }
                                          touched={
                                            form.touched
                                              .hasIntervalBeforeNextAttempt
                                          }
                                          form={form}
                                          label=""
                                          helperText=""
                                        >
                                          <Checkbox
                                            size="lg"
                                            mr={5}
                                            isChecked={field.value}
                                            onChange={(e) =>
                                              setFormInputs({
                                                ...formInputs,
                                                hasIntervalBeforeNextAttempt:
                                                  e.target.checked,
                                              })
                                            }
                                          >
                                            Interval Before Next Attempt
                                          </Checkbox>
                                        </FormikFormControl>
                                      )}
                                    </Field>
                                  </Box>
                                  <Box>
                                    {formInputs.hasIntervalBeforeNextAttempt ===
                                      true && (
                                      <Flex align="center">
                                        <Box>
                                          <Flex>
                                            <Box w="70px" mr={2}>
                                              <Text
                                                fontWeight="medium"
                                                fontSize="sm"
                                              >
                                                days
                                              </Text>
                                              <NumberInput
                                                min={0}
                                                size="md"
                                                onChange={(value) =>
                                                  setTimeBeforeNextAttempt({
                                                    ...timeBeforeNextAttempt,
                                                    days: value,
                                                  })
                                                }
                                                value={
                                                  timeBeforeNextAttempt.days
                                                }
                                              >
                                                <NumberInputField />
                                                <NumberInputStepper>
                                                  <NumberIncrementStepper />
                                                  <NumberDecrementStepper />
                                                </NumberInputStepper>
                                              </NumberInput>
                                            </Box>
                                            <Box w="70px" mr={2}>
                                              <Text
                                                fontWeight="medium"
                                                fontSize="sm"
                                              >
                                                hours
                                              </Text>
                                              <NumberInput
                                                min={0}
                                                max={23}
                                                size="md"
                                                onChange={(value) =>
                                                  setTimeBeforeNextAttempt({
                                                    ...timeBeforeNextAttempt,
                                                    hours: value,
                                                  })
                                                }
                                                value={
                                                  timeBeforeNextAttempt.hours
                                                }
                                              >
                                                <NumberInputField />
                                                <NumberInputStepper>
                                                  <NumberIncrementStepper />
                                                  <NumberDecrementStepper />
                                                </NumberInputStepper>
                                              </NumberInput>
                                            </Box>
                                            <Box w="70px" mr={2}>
                                              <Text
                                                fontWeight="medium"
                                                fontSize="sm"
                                              >
                                                minutes
                                              </Text>
                                              <NumberInput
                                                defaultValue={0}
                                                min={0}
                                                max={59}
                                                size="md"
                                                onChange={(value) =>
                                                  setTimeBeforeNextAttempt({
                                                    ...timeBeforeNextAttempt,
                                                    minutes: value,
                                                  })
                                                }
                                                value={
                                                  timeBeforeNextAttempt.minutes
                                                }
                                              >
                                                <NumberInputField />
                                                <NumberInputStepper>
                                                  <NumberIncrementStepper />
                                                  <NumberDecrementStepper />
                                                </NumberInputStepper>
                                              </NumberInput>
                                            </Box>
                                          </Flex>
                                        </Box>
                                      </Flex>
                                    )}
                                  </Box>
                                </Flex>
                                <Flex>
                                  <Text fontSize="sm" color="gray">
                                    Set a waiting period before participants can
                                    retake the quiz.
                                  </Text>
                                </Flex>
                              </Box>
                              <Box flex="1">
                                <Flex>
                                  <Box flex="1" mr={5}>
                                    <Field name="scoreType">
                                      {({ field, form }) => (
                                        <FormikFormControl
                                          label="Score Type"
                                          helperText="Compute scores as a percentage or as an actual score."
                                          errors={form.errors.scoreType}
                                          touched={form.touched.scoreType}
                                          form={form}
                                        >
                                          <Select
                                            onChange={(e) =>
                                              setFormInputs({
                                                ...formInputs,
                                                scoreType: e.target.value,
                                              })
                                            }
                                            value={field.value}
                                          >
                                            <option value="Actual Score">
                                              Actual Score
                                            </option>
                                            <option value="Percentage">
                                              Percentage
                                            </option>
                                          </Select>
                                        </FormikFormControl>
                                      )}
                                    </Field>
                                  </Box>
                                  <Box flex="1" mr={5}>
                                    <Field name="passingScore">
                                      {({ field, form }) => (
                                        <FormikFormControl
                                          label={
                                            formInputs.scoreType ===
                                            'Percentage'
                                              ? 'Passing Score (%)'
                                              : 'Passing Score'
                                          }
                                          helperText="Define the score needed to pass the quiz based on the Score Setting."
                                          errors={form.errors.passingScore}
                                          touched={form.touched.passingScore}
                                          form={form}
                                        >
                                          <NumberInput
                                            min={0}
                                            max={100}
                                            w="100px"
                                            onChange={(value) =>
                                              setFormInputs({
                                                ...formInputs,
                                                passingScore:
                                                  value === ''
                                                    ? 0
                                                    : parseInt(value, 10),
                                              })
                                            }
                                            value={field.value}
                                          >
                                            <NumberInputField {...field} />
                                            <NumberInputStepper>
                                              <NumberIncrementStepper />
                                              <NumberDecrementStepper />
                                            </NumberInputStepper>
                                          </NumberInput>
                                        </FormikFormControl>
                                      )}
                                    </Field>
                                  </Box>
                                </Flex>
                                <Flex mt={5}>
                                  <Box w="50%">
                                    <Field name="showResult">
                                      {({ field, form }) => (
                                        <FormikFormControl
                                          errors={form.errors.showResult}
                                          touched={form.touched.showResult}
                                          form={form}
                                          label=""
                                          helperText=""
                                        >
                                          <Checkbox
                                            size="lg"
                                            mr={5}
                                            isChecked={field.value}
                                            onChange={(e) =>
                                              setFormInputs({
                                                ...formInputs,
                                                showResult: e.target.checked,
                                              })
                                            }
                                          >
                                            Show Result
                                          </Checkbox>
                                        </FormikFormControl>
                                      )}
                                    </Field>
                                    <Text fontSize="sm" color="gray">
                                      Display scores right after the quiz for
                                      instant feedback.
                                    </Text>
                                  </Box>
                                  <Box w="50%">
                                    <Field name="showAnswers">
                                      {({ field, form }) => (
                                        <FormikFormControl
                                          errors={form.errors.showAnswers}
                                          touched={form.touched.showAnswers}
                                          form={form}
                                          label=""
                                          helperText=""
                                        >
                                          <Checkbox
                                            size="lg"
                                            mr={5}
                                            isChecked={field.value}
                                            onChange={(e) =>
                                              setFormInputs({
                                                ...formInputs,
                                                showAnswers: e.target.checked,
                                              })
                                            }
                                          >
                                            Show Answers
                                          </Checkbox>
                                        </FormikFormControl>
                                      )}
                                    </Field>
                                    <Text fontSize="sm" color="gray">
                                      Allow participants to view correct answers
                                      after the quiz.
                                    </Text>
                                  </Box>
                                </Flex>
                              </Box>
                            </Flex>
                            <Flex>
                              <Spacer />
                            </Flex>
                          </Form>
                        </Box>
                      )}
                    </Formik>
                  </TabPanel>
                  <TabPanel>
                    {tabIndex === 2 && (
                      <QuestionAdminList
                        questions={questions}
                        formInputs={formInputs}
                        mode={CREATE_MODE}
                        onAddQuestion={handleAddQuestion}
                        onUpdateQuestion={handleUpdateQuestion}
                        onRemoveQuestion={handleRemoveQuestion}
                        onQuestionTypeChange={handleQuestionTypeChange}
                        onToggleCollapseExpand={handleToggleCollapseExpand}
                        allQuestionsCollapsed={allQuestionsCollapsed}
                        forceCollapseAll={forceCollapseAll}
                        forceExpandAll={forceExpandAll}
                        onQuestionsReorder={handleQuestionsReorder}
                        onImportQuestions={handleImportQuestions}
                      />
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>

              <Divider mt={6} />

              {/* Footer actions similar to modal footer */}
              <Box
                mt={4}
                position="sticky"
                bottom={0}
                bg={footerBg}
                py={3}
                mr={20}
              >
                <Flex justify="flex-end">
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    mr={3}
                    onClick={() => {
                      navigate(`/admin/courses/${courseId}`);
                    }}
                    isLoading={
                      createQuiz.status === ReducerStatus.LOADING ||
                      createQuizQuestions.status === ReducerStatus.LOADING
                    }
                  >
                    Close
                  </Button>
                  <Button
                    type="button"
                    colorScheme="red"
                    isDisabled={tabIndex === 0}
                    isLoading={
                      createQuiz.status === ReducerStatus.LOADING ||
                      createQuizQuestions.status === ReducerStatus.LOADING
                    }
                    px={10}
                    mr={4}
                    onClick={() => {
                      handleTabsChange(tabIndex - 1);
                    }}
                    variant="outline"
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    colorScheme="red"
                    isDisabled={tabIndex === 2}
                    isLoading={
                      createQuiz.status === ReducerStatus.LOADING ||
                      createQuizQuestions.status === ReducerStatus.LOADING
                    }
                    px={10}
                    mr={4}
                    onClick={() => {
                      handleTabsChange(tabIndex + 1);
                    }}
                    variant="outline"
                  >
                    Next
                  </Button>
                  <Spacer />
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    mr={4}
                    isLoading={
                      createQuiz.status === ReducerStatus.LOADING ||
                      createQuizQuestions.status === ReducerStatus.LOADING
                    }
                    onClick={handleClearAll}
                  >
                    Clear Draft
                  </Button>
                  <Button
                    mr={4}
                    isLoading={
                      createQuiz.status === ReducerStatus.LOADING ||
                      createQuizQuestions.status === ReducerStatus.LOADING
                    }
                    variant="outline"
                    colorScheme="red"
                    onClick={() => {
                      handleSaveDraft();
                    }}
                  >
                    Save Draft
                  </Button>
                  <Button
                    isLoading={
                      createQuiz.status === ReducerStatus.LOADING ||
                      createQuizQuestions.status === ReducerStatus.LOADING
                    }
                    onClick={handleTriggerSubmit}
                    colorScheme="red"
                    px={10}
                  >
                    Submit
                  </Button>
                </Flex>
              </Box>

              {/* Result modal preserved */}
              {(createQuiz.status === ReducerStatus.SUCCEEDED ||
                createQuizQuestions.status === ReducerStatus.SUCCEEDED ||
                createQuiz.status === ReducerStatus.FAILED) && (
                <ResultModal
                  isOpen
                  onSubmit={handleResult}
                  isError={createQuiz.status === ReducerStatus.FAILED}
                  title={
                    createQuiz.status === ReducerStatus.SUCCEEDED ||
                    createQuizQuestions.status === ReducerStatus.SUCCEEDED
                      ? 'Quiz Sub-Quest Created'
                      : null
                  }
                  message={
                    createQuiz.status === ReducerStatus.SUCCEEDED ||
                    createQuizQuestions.status === ReducerStatus.SUCCEEDED
                      ? 'The quiz sub-quest has been created successfully.'
                      : createQuiz.error?.data?.title || 'Server Error'
                  }
                  errorObject={
                    createQuiz.error?.data?.errors ||
                    createQuizQuestions?.data?.errors
                  }
                />
              )}
            </Box>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}

export default CreateQuizPage;
